import * as React from 'react';
import { z } from "zod";
import { useFormContext } from "react-hook-form";
import Typography from '@mui/material/Typography';
import {
  Container, Button, Box, IconButton, Grid,
  Select, MenuItem, Alert, Snackbar, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // For auto-generating tables in PDF
import api, {
  fetchTechnicalComplaints, updateTechnicalComplaint, emailTechnicalComplaintPdf,
  fetchCustomers, fetchVehicles, fetchSolutions, fetchUsers, fetchCustomerSupportComplaint,
} from "../services/ApiService";
import dayjs from 'dayjs'; // Install with npm: npm install dayjs
import BulkUpload from '../components/BulkUploadOperation';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import DataTable from '../components/DataTable';
import { FormModal, RHFTextField, RHFSelect } from "../components/form";

// ─── Zod schemas ────────────────────────────────────────────────────────────

const addSchema = z.object({
  logged_date: z.string().min(1, "Required"),
  resolution_date: z.string().min(1, "Required"),
  device: z.string().min(1, "Required"),
  haulier: z.string().min(1, "Required"),
  vehicle: z.string().min(1, "Required"),
  issue: z.string().min(1, "Required"),
  location: z.string().min(1, "Required"),
  status: z.string().min(1, "Required"),
  category: z.string().optional(),
  reason: z.string().optional(),
  remarks: z.string().optional(),
  resource_name: z.string().optional(),
  scope: z.string().optional(),
  part_description: z.string().optional(),
}).superRefine((d, ctx) => {
  if (d.status === "Pending") {
    if (!d.scope) ctx.addIssue({ path: ["scope"], message: "Required", code: "custom" });
    if (!d.part_description) ctx.addIssue({ path: ["part_description"], message: "Required", code: "custom" });
  }
});

const editSchema = z.object({
  resolution_date: z.string().min(1, "Required"),
  issue: z.string().min(1, "Required"),
  location: z.string().min(1, "Required"),
  category: z.string().optional(),
  reason: z.string().optional(),
  scope: z.string().optional(),
  part_description: z.string().optional(),
  resource_name: z.string().optional(),
});

// ─── Helper component: watches haulier field and loads trucks for that haulier ──

const HaulierWatcher = ({ setTrucks }) => {
  const { watch, setValue } = useFormContext();
  const haulier = watch("haulier");
  React.useEffect(() => {
    if (!haulier) return;
    setValue("vehicle", "");
    fetchVehicles(haulier)
      .then((response) => {
        const dict = response.data.reduce((acc, item) => {
          acc[item.id] = item.registration_number;
          return acc;
        }, {});
        setTrucks(dict);
      })
      .catch((err) => console.error("Error fetching trucks:", err));
  }, [haulier, setValue, setTrucks]);
  return null;
};

// ─── Page component ──────────────────────────────────────────────────────────

export function TechnicalComplaint() {
  const [sendingEmailId, setSendingEmailId] = React.useState(null);

  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [selectedRow, setSelectedRow] = React.useState(null);
  const [editModalOn, setEditModalOn] = React.useState(false);
  const handleEditOff = () => setEditModalOn(false);
  const handleEditOn = (row) => {
    console.log(row);
    console.log(typeof (row));
    setEditModalOn(true);
    setSelectedRow(row);
  };

  React.useEffect(() => {
    fetchHauliers();
    fetchTruck();
    fetchDeliveries();
    fetchDevices();
    loadUsers();
  }, []);

  const [haulierOptions, setHaulierOptions] = React.useState([]);
  const [truckOptions, setTruckOptions] = React.useState([]);
  const [devicesOptions, setDevicesOptions] = React.useState([]);
  const [usersOptions, setUsersOptions] = React.useState([]);
  const [selectedCustomerSupport, setCustomerSupportComplain] = React.useState(null);
  const [cscModelOpen, setCSCModalOpen] = React.useState(false);
  const [users, setUsers] = React.useState([]);

  const loadUsers = async () => {
    try {
      const response = await fetchUsers();
      const deviceDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      setUsersOptions(deviceDict);
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users", error);
    }
  };

  const handleViewCustomerSupportComplainDetail = async (saleId) => {
    try {
      const response = await fetchCustomerSupportComplaint(saleId);
      console.log(response.data);
      setCustomerSupportComplain(response.data);
      setCSCModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch sale detail:", error);
    }
  };

  const handleSendEmail = async (saleId) => {
    setSendingEmailId(saleId);
    try {
      await emailTechnicalComplaintPdf(saleId, {});
      alert("Email sent successfully!");
    } catch (error) {
      console.error("Email error:", error);
      alert("Failed to send email.");
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleStatusChange = async (saleId, newStatus, sale) => {
    const updatedSale = { ...sale, status: newStatus };
    try {
      await updateTechnicalComplaint(saleId, updatedSale);
      setEmployees((prevData) =>
        prevData.map((item) =>
          item.id === saleId ? { ...item, status: newStatus } : item
        )
      );
      showSnackbar('Status updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const fetchDeliveries = async () => {
    try {
      const response = await fetchTechnicalComplaints();
      console.log(response.data);
      setEmployees(response.data);
      showSnackbar('Hauliers fetched successfully', 'success');
    } catch (error) {
      const errorMessage =
        error.response?.statusText || error.message || 'Error fetching hauliers';
      showSnackbar(`Failed to fetch hauliers: ${errorMessage}`, 'error');
    }
  };

  const fetchHauliers = async () => {
    try {
      const response = await fetchCustomers();
      const allNames = response.data.map((employee) => employee.client_name);
      const haulierDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      setHaulierOptions(haulierDict);
      setHauliers(allNames);
      showSnackbar('Hauliers fetched successfully', 'success');
    } catch (error) {
      const errorMessage =
        error.response?.statusText || error.message || 'Error fetching hauliers';
      showSnackbar(`Failed to fetch hauliers: ${errorMessage}`, 'error');
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await fetchSolutions();
      const allNames = response.data.map((employee) => employee.name);
      const deviceDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      setDevicesOptions(deviceDict);
      setDevice(allNames);
      showSnackbar('Hauliers fetched successfully', 'success');
    } catch (error) {
      const errorMessage =
        error.response?.statusText || error.message || 'Error fetching hauliers';
      showSnackbar(`Failed to fetch hauliers: ${errorMessage}`, 'error');
    }
  };

  const fetchTruck = async () => {
    try {
      const response = await fetchVehicles();
      const allNames = response.data.map((employee) => employee.name);
      const haulierDict = response.data.reduce((acc, item) => {
        acc[item.id] = item.registration_number;
        return acc;
      }, {});
      setTruckOptions(haulierDict);
      setTrucks(allNames);
      showSnackbar('Hauliers fetched successfully', 'success');
    } catch (error) {
      const errorMessage =
        error.response?.statusText || error.message || 'Error fetching hauliers';
      showSnackbar(`Failed to fetch hauliers: ${errorMessage}`, 'error');
    }
  };

  const [device, setDevice] = React.useState([]);
  const [haulier, setHauliers] = React.useState([]);
  const [trucks, setTrucks] = React.useState([]);
  const [employees, setEmployees] = React.useState([]);
  const [modalOpen, setModalOpen] = React.useState(false);

  // Save employees to localStorage on update
  React.useEffect(() => {
    // localStorage.setItem('employees', JSON.stringify(employees));
  }, [employees]);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const handleExport = () => {
    const doc = new jsPDF('l', 'mm', 'a2'); // Landscape A2 for wider table

    // Title
    doc.setFontSize(18);
    doc.text('Technical Complain Report', 14, 16);

    // Match UI columns
    const columns = [
      'Operation #',
      'Complain #',
      'Category',
      'Issue',
      'Location',
      'Resource Name',
      'Resolution Date',
      'Scope',
      'Part Description',
      'Status',
    ];

    // Build table rows
    const tableData = employees.map((row) => [
      row.id,
      row.complain || "",
      row.category || "",
      row.issue || "",
      row.location || "",
      usersOptions[row.resource_name] || "",
      formatDatetimeLocal(row.resolution_date) || "",
      row.status === "Pending" ? row.scope || "-" : "-",
      row.status === "Pending" ? row.part_description || "-" : "-",
      row.status || "",
    ]);

    // Generate table
    doc.autoTable({
      head: [columns],
      body: tableData,
      startY: 30,
      styles: {
        fontSize: 9,
        cellPadding: 2.5,
      },
      columnStyles: {
        0: { cellWidth: 25 },  // Operation #
        1: { cellWidth: 30 },  // Complain #
        2: { cellWidth: 30 },  // Category
        3: { cellWidth: 40 },  // Issue
        4: { cellWidth: 30 },  // Location
        5: { cellWidth: 35 },  // Resource Name
        6: { cellWidth: 40 },  // Resolution Date
        7: { cellWidth: 30 },  // Scope
        8: { cellWidth: 40 },  // Part Description
        9: { cellWidth: 25 },  // Status
      },
      theme: 'grid',
      didDrawPage: () => {
        doc.setFontSize(18);
        doc.text('Technical Complain Report', 14, 16);
      },
    });

    doc.save('Technical_Complain_Report.pdf');
  };

  // ─── Add modal ─────────────────────────────────────────────────────────────

  const EmployeeModal = ({ open, handleClose }) => {
    const [addTrucks, setAddTrucks] = React.useState({});
    const [addImageFile, setAddImageFile] = React.useState(null);

    return (
      <FormModal
        open={open}
        onClose={handleClose}
        title="Attach Device"
        schema={addSchema}
        defaultValues={{
          logged_date: "",
          resolution_date: "",
          device: "",
          haulier: "",
          vehicle: "",
          issue: "",
          location: "",
          status: "",
          category: "",
          reason: "",
          remarks: "",
          resource_name: "",
          scope: "",
          part_description: "",
        }}
        onSubmit={async (data) => {
          const fd = new FormData();
          const formattedData = {
            ...data,
            logged_date: dayjs(data.logged_date).format("YYYY-MM-DDTHH:mm:ss"),
            resolution_date: dayjs(data.resolution_date).format("YYYY-MM-DDTHH:mm:ss"),
          };
          Object.entries(formattedData).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== "") fd.append(k, v);
          });
          if (addImageFile) fd.append("image", addImageFile);
          try {
            await api.post("/technical-complaint/", fd);
            showSnackbar("Added successfully", "success");
            fetchDeliveries();
            setAddImageFile(null);
          } catch (err) {
            showSnackbar("Failed to add complaint", "error");
            throw err;
          }
        }}
        submitLabel="Add"
        width={400}
      >
        {({ control, watch }) => {
          const status = watch("status");
          const deviceVal = watch("device");
          return (
            <>
              <HaulierWatcher setTrucks={setAddTrucks} />
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <RHFTextField
                    control={control}
                    name="logged_date"
                    label="Logged Date"
                    type="datetime-local"
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField
                    control={control}
                    name="resolution_date"
                    label="Resolution Date"
                    type="datetime-local"
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFSelect control={control} name="device" label="Device" options={devicesOptions} required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFSelect control={control} name="haulier" label="Haulier" options={haulierOptions} required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFSelect control={control} name="vehicle" label="Truck" options={addTrucks} required />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFSelect control={control} name="category" label="Category">
                    {deviceVal == "1"
                      ? [
                          <MenuItem key="NR" value="NR">NR</MenuItem>,
                          <MenuItem key="Camera" value="Camera">Camera</MenuItem>,
                          <MenuItem key="SD Card" value="SD Card">SD Card</MenuItem>,
                          <MenuItem key="GPS" value="GPS">GPS</MenuItem>,
                          <MenuItem key="SIM" value="SIM">SIM</MenuItem>,
                          <MenuItem key="Device" value="Device">Device</MenuItem>,
                          <MenuItem key="Other" value="Other">Other</MenuItem>,
                        ]
                      : [
                          <MenuItem key="NR2" value="NR">NR</MenuItem>,
                          <MenuItem key="Electrical" value="Electrical">Electrical</MenuItem>,
                          <MenuItem key="Mechanical" value="Mechanical">Mechanical</MenuItem>,
                          <MenuItem key="GPGSM" value="GPS/GSM">GPS/GSM</MenuItem>,
                          <MenuItem key="Sensor" value="Sensor">Sensor</MenuItem>,
                          <MenuItem key="Sim" value="Sim">Sim</MenuItem>,
                          <MenuItem key="Barrier" value="Barrier">Barrier</MenuItem>,
                          <MenuItem key="Device2" value="Device">Device</MenuItem>,
                        ]}
                  </RHFSelect>
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField control={control} name="issue" label="Issue" required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField control={control} name="location" label="Location" required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField control={control} name="status" label="Status" required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField control={control} name="reason" label="Reason" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField control={control} name="remarks" label="Remarks" />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFSelect control={control} name="resource_name" label="Resource Name">
                    {users
                      .filter((u) => u.department === "Monit")
                      .map((u) => (
                        <MenuItem key={u.id} value={String(u.id)}>
                          {u.name}
                        </MenuItem>
                      ))}
                  </RHFSelect>
                </Grid>

                {status === "Pending" && (
                  <>
                    <Grid item xs={12} md={6}>
                      <RHFSelect control={control} name="scope" label="Scope" required>
                        <MenuItem value="Repair">Repair</MenuItem>
                        <MenuItem value="Replacement">Replacement</MenuItem>
                      </RHFSelect>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <RHFTextField control={control} name="part_description" label="Part Description" required />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Image (optional)
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ width: "100%" }}
                      onChange={(e) => setAddImageFile(e.target.files?.[0] ?? null)}
                    />
                  </Box>
                </Grid>
              </Grid>
            </>
          );
        }}
      </FormModal>
    );
  };

  function getBase64Image(imgPath) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = imgPath; // Ensure this path points to your public folder image

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL(); // This will give you the base64 string
        resolve(dataUrl); // Return the complete base64 string (including header)
      };

      img.onerror = reject;
    });
  }

  const handleExportInvoice = (record) => {
    const doc = new jsPDF();

    // Create a blue strip at the top with the word 'Invoice'
    doc.setFillColor(0, 123, 255); // Set blue color for the strip (RGB)
    doc.rect(0, 0, 210, 20, 'F'); // Create a filled rectangle (top strip)

    // Add 'Invoice' text on the blue strip at the top, aligned left
    doc.setFontSize(24); // Increase font size
    doc.setFont('helvetica', 'bold'); // Set font to bold
    doc.setTextColor(255, 255, 255); // White text color
    doc.text('Invoice', 14, 14); // Left-aligned text (positioned at 14, 14)

    // Add Logo Image to PDF (start after the top strip)
    getBase64Image('/logo2.png').then((base64) => {
      // Add the image at the top (position at x=14, y=25, just after the blue strip)
      doc.addImage(base64, 'PNG', 14, 25, 50, 20); // Adjust width and height as needed
      let yPosition = 55; // Starting Y position for data below the logo

      // Add Invoice Date and To row
      doc.setFontSize(12); // Adjust size for the row text
      doc.setFont('helvetica', 'normal'); // Set font to normal
      doc.setTextColor(0, 0, 0); // Black text color

      // Invoice Date and To (with space in between)
      doc.text('Invoice Date:', 14, yPosition); // Label for Invoice Date
      doc.text(record.date, 40, yPosition); // Value of Invoice Date

      // Add space and then 'To' label
      doc.text('To:', 140, yPosition); // Label for To
      doc.text(record.haulier, 150, yPosition); // Value for To (Haulier)

      // Increase Y position for the next field after this row
      yPosition += 10;

      // Key-Value style (starting from the next line after the table)
      const fieldNames = [
        'Time', 'Truck', 'Expiry Date', 'Remarks'
      ];

      const fieldValues = [
        record.time, record.vehicle, record.expiryDate, record.remarks
      ];

      // Loop through the fields and print them as key-value pairs
      for (let i = 0; i < fieldNames.length; i++) {
        doc.setFontSize(12); // Adjusted size for field values
        doc.text(`${fieldNames[i]}:`, 14, yPosition); // Field name (Key)
        doc.text(fieldValues[i], 70, yPosition); // Field value
        yPosition += 10; // Increase Y position for the next field
      }

      // Table for Device ID and Cost
      doc.setFontSize(12);
      const tableStartY = yPosition; // Save the starting Y position for the table
      const cellHeight = 10; // Height for each table row

      // Table headers
      doc.setFont('helvetica', 'bold');
      doc.text('Device ID', 14, tableStartY);
      // Draw horizontal line under headers
      doc.line(14, tableStartY + 1, 190, tableStartY + 1); // Horizontal line below the headers

      // Table content
      doc.setFont('helvetica', 'normal');
      doc.text(record.device, 14, tableStartY + cellHeight);

      // Draw horizontal line under the table content
      doc.line(14, tableStartY + cellHeight + 1, 190, tableStartY + cellHeight + 1); // Line under the values

      // Table content
      doc.setFont('helvetica', 'normal');
      doc.text("Subtotal", 14, tableStartY + 2 * cellHeight);

      // Draw horizontal line under the table content
      doc.line(14, tableStartY + 2 * cellHeight + 1, 190, tableStartY + 2 * cellHeight + 1); // Line under the values

      // Increase Y position for the next section after the table
      yPosition = tableStartY + 2 * cellHeight; // Update yPosition after table

      // Create a blue strip at the bottom with the word 'Invoice'
      doc.setFillColor(0, 123, 255); // Set blue color for the strip (RGB)
      doc.rect(0, 285, 210, 20, 'F'); // Create a filled rectangle (top strip)
      // Add 'Invoice' text on the blue strip at the bottom, aligned left
      doc.setFontSize(12); // Increase font size
      doc.setFont('helvetica', 'bold'); // Set font to bold
      doc.setTextColor(255, 255, 255); // White text color
      doc.text('This is a computer-generated invoice. It does not require any signature.', 33, 293); // Left-aligned text (positioned at 14, 293)

      record.generated = true;

      // Save the document
      doc.save('Device_Report.pdf');
    }).catch((err) => {
      console.error('Error loading image:', err);
    });
  };

  // ─── Edit modal ────────────────────────────────────────────────────────────

  const EditOperationModal = ({ open, handleClose, selectedRow }) => {
    const [editImageFile, setEditImageFile] = React.useState(null);

    return (
      <FormModal
        open={open}
        onClose={handleClose}
        title="Edit Operation"
        schema={editSchema}
        defaultValues={{
          resolution_date: formatDatetimeLocal(selectedRow?.resolution_date ?? ""),
          category: selectedRow?.category ?? "",
          issue: selectedRow?.issue ?? "",
          location: selectedRow?.location ?? "",
          reason: selectedRow?.reason ?? "",
          scope: selectedRow?.scope ?? "",
          part_description: selectedRow?.part_description ?? "",
          resource_name: selectedRow?.resource_name ? String(selectedRow.resource_name) : "",
          // status is not in the schema but included in form state for watch()
          status: selectedRow?.status ?? "",
        }}
        onSubmit={async (data) => {
          const fd = new FormData();
          const formattedData = {
            ...data,
            resolution_date: dayjs(data.resolution_date).format("YYYY-MM-DDTHH:mm:ss"),
          };
          Object.entries(formattedData).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== "") fd.append(k, v);
          });
          // status is display-only; send the original value from selectedRow
          fd.append("status", selectedRow.status);
          if (editImageFile) fd.append("image", editImageFile);
          try {
            await updateTechnicalComplaint(selectedRow.id, fd);
            showSnackbar("Updated successfully", "success");
            fetchDeliveries();
            setEditImageFile(null);
          } catch (err) {
            showSnackbar("Failed to update", "error");
            throw err;
          }
        }}
        submitLabel="Update"
        width={400}
      >
        {({ control, watch }) => {
          const status = watch("status");
          return (
            <>
              {/* Timestamp bar */}
              <Box
                sx={{
                  mb: 2,
                  pb: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  gap: 3,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2">
                  Updated At: {selectedRow?.updated_at?.slice(0, 19).replace("T", " ")}
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                  Created At: {selectedRow?.created_at?.slice(0, 19).replace("T", " ")}
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <RHFTextField
                    control={control}
                    name="resolution_date"
                    label="Resolution Date"
                    type="datetime-local"
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFSelect control={control} name="category" label="Category">
                    {selectedRow?.device == "1"
                      ? [
                          <MenuItem key="NR" value="NR">NR</MenuItem>,
                          <MenuItem key="Camera" value="Camera">Camera</MenuItem>,
                          <MenuItem key="SD Card" value="SD Card">SD Card</MenuItem>,
                          <MenuItem key="GPS" value="GPS">GPS</MenuItem>,
                          <MenuItem key="SIM" value="SIM">SIM</MenuItem>,
                          <MenuItem key="Device1" value="Device">Device</MenuItem>,
                        ]
                      : [
                          <MenuItem key="NR2" value="NR">NR</MenuItem>,
                          <MenuItem key="Electrical" value="Electrical">Electrical</MenuItem>,
                          <MenuItem key="Mechanical" value="Mechanical">Mechanical</MenuItem>,
                          <MenuItem key="GPGSM" value="GPS/GSM">GPS/GSM</MenuItem>,
                          <MenuItem key="Sensor" value="Sensor">Sensor</MenuItem>,
                          <MenuItem key="Sim" value="Sim">Sim</MenuItem>,
                          <MenuItem key="Barrier" value="Barrier">Barrier</MenuItem>,
                          <MenuItem key="Device2" value="Device">Device</MenuItem>,
                        ]}
                  </RHFSelect>
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField control={control} name="issue" label="Issue" required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField control={control} name="location" label="Location" required />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField control={control} name="reason" label="Reason" />
                </Grid>

                {status === "Pending" && (
                  <>
                    <Grid item xs={12} md={6}>
                      <RHFSelect control={control} name="scope" label="Scope" required>
                        <MenuItem value="Repair">Repair</MenuItem>
                        <MenuItem value="Replacement">Replacement</MenuItem>
                      </RHFSelect>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <RHFTextField control={control} name="part_description" label="Part Description" required />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} md={6}>
                  <RHFSelect control={control} name="resource_name" label="Resource Name">
                    {users
                      .filter((u) => u.department === "Monit")
                      .map((u) => (
                        <MenuItem key={u.id} value={String(u.id)}>
                          {u.name}
                        </MenuItem>
                      ))}
                  </RHFSelect>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Image (optional)
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ width: "100%" }}
                      onChange={(e) => setEditImageFile(e.target.files?.[0] ?? null)}
                    />
                  </Box>
                </Grid>
              </Grid>
            </>
          );
        }}
      </FormModal>
    );
  };

  function formatDatetimeLocal(iso) {
    if (!iso) return "";
    const dt = new Date(iso);
    return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  }

  return (
    <Container maxWidth="xl" sx={{ m: 2 }}>
      <EmployeeModal
        open={modalOpen}
        handleClose={handleClose}
      />
      {editModalOn && selectedRow && (
        <EditOperationModal
          selectedRow={selectedRow}
          open={editModalOn}
          handleClose={handleEditOff}
        />
      )}
      <DataTable
        title="Technical Complain List"
        columns={[
          { key: 'id', label: 'Operation #' },
          { key: 'complain', label: 'Complain #', render: (row) => (
            <span onClick={() => handleViewCustomerSupportComplainDetail(row.complain)} style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}>{row.complain}</span>
          )},
          { key: 'category', label: 'Category' },
          { key: 'issue', label: 'Issue' },
          { key: 'location', label: 'Location' },
          { key: 'resource_name', label: 'Resource Name', render: (row) => usersOptions[row.resource_name] },
          { key: 'resolution_date', label: 'Resolution Date', render: (row) => formatDatetimeLocal(row.resolution_date) },
          { key: 'scope', label: 'Scope', render: (row) => row.status === "Pending" ? row.scope || "-" : "-" },
          { key: 'part_description', label: 'Part Description', render: (row) => row.status === "Pending" ? row.part_description || "-" : "-" },
        ]}
        rows={employees}
        toolbarActions={<Button variant="contained" color="primary" style={{ marginLeft: "10px" }} onClick={handleExport}>Export to PDF</Button>}
        renderActions={(row) => (
          <Box display="flex" gap="6px">
            <IconButton color="secondary" size="small" onClick={() => handleEditOn(row)} sx={{ padding: "6px" }}><EditIcon /></IconButton>
            <Select fullWidth value={row.status} onChange={(e) => handleStatusChange(row.id, e.target.value, row)} size="small" sx={{ ml: 1, minWidth: 100 }}>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Complete">Complete</MenuItem>
            </Select>
            {row.status === "Pending" && (
              <IconButton color="primary" size="small" onClick={() => handleSendEmail(row.id)} sx={{ padding: "6px", marginLeft: "4px" }} disabled={sendingEmailId === row.id}>
                {sendingEmailId === row.id ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              </IconButton>
            )}
          </Box>
        )}
      />

      <Dialog open={cscModelOpen} onClose={() => setCSCModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sale Details</DialogTitle>
        <DialogContent>
          {selectedCustomerSupport ? (
            <Box>
              <Typography variant="body1"><strong>Customer:</strong> {haulierOptions[selectedCustomerSupport.customer]}</Typography>
              <Typography variant="body1"><strong>Vehicle:</strong> {truckOptions[selectedCustomerSupport.vehicle]}</Typography>
              <Typography variant="body1"><strong>IMEI:</strong> {selectedCustomerSupport.imei}</Typography>
              <Typography variant="body1"><strong>Complainant:</strong> {selectedCustomerSupport.complainant}</Typography>
              <Typography variant="body1"><strong>Category:</strong> {selectedCustomerSupport.category}</Typography>
              <Typography variant="body1"><strong>Details:</strong> {selectedCustomerSupport.details}</Typography>
            </Box>
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCSCModalOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
